import { head, path, mergeAll, isEmpty } from 'ramda'
import { memo, useRef, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import { useHistory } from 'react-router-dom'
import { getPrimaryOther, getChatAvatar } from '@/features/Chat/utils'
import useGetSuiteInfo from '@/hooks/get-suite-info'
import useRole from '@/hooks/use-role'
import { getImageUrl } from '@/utils/image'
import { show as showTimelineMembersModal } from '@/features/Chat/timeline-members-modal'
import { getUrl } from '@/utils/listings'
import { select } from '@/store'
import hideUnit from '@/utils/hide-unit'
import formatName from '@/utils/format-name'
import config from '@/config'
import useSearchParams from '@/hooks/use-search-params'
import { show as showEndTenancyModal } from '@/features/EndTenancy/end-tenancy-modal'
import { show as showPaymentDetailsModal } from '@/features/Payments/payment-details-modal'
import BaseChatRoom from './base'
import MessageDetailsDrawer from './message-details-drawer'

const { APP_PATH, WEB_URL } = config

function buildScrollPosition(element) {
  const oldPosition = element.offsetTop
  const oldScroll = element.scrollTop
  return function () {
    return element.offsetTop - oldPosition + oldScroll
  }
}

const ChatRoom = ({ handleBack }) => {
  const { t } = useTranslation()
  const loadedMore = useRef(false)
  const scrollMoreEnabled = useRef(false)
  const params = useParams()
  const history = useHistory()
  const dispatch = useDispatch()

  const { state } = useLocation()
  const isSearchResult = useSelector(path(['chat', 'current_timeline', 'is_search_result']))
  const is_favourite = useSelector(path(['chat', 'current_timeline', 'is_favourite'])) === '1'
  const searchParams = useSearchParams()
  const timelineId = params.timelineId
  const timeline = useSelector(state => select.chat.timeline(state, { timelineId }))
  const lastEventId = timeline?.last_event_message?.id
  const listing = useGetSuiteInfo(timeline?.listing_id)
  const [loading, setLoading] = useState(false)
  const [loadingBottom, setLoadingBottom] = useState(false)
  const [sendingInvite, setSendingInvite] = useState(false)
  const [isMessageDrawerOpen, setIsMessageDrawerOpen] = useState(false)
  const [activeTimelineEvent, setActiveTimelineEvent] = useState(null)
  const role = useRole()
  const isLandlord = role === 'landlord'
  const eventsSearch = useSelector(path(['chat', 'eventsSearch']))
  const timelinesAreLoaded = useSelector(path(['chat', 'intialTimelinesLoaded']))
  const timelineLoaded =
    !!timeline && timelinesAreLoaded && (!isSearchResult || !isEmpty(eventsSearch))
  const presetMessages = useSelector(select.chatPreset.presets)

  const timelineEvents = useSelector(state => select.chat.timelineEvents(state, { timelineId }))

  const lastId = path(['0', 'id'], timelineEvents)
  const latestId = path([timelineEvents.length - 1, 'id'], timelineEvents)
  const targetMessageId = state?.targetMessageId
  const targetIsLastMessageFirstLoad = !timelineLoaded && targetMessageId === latestId

  useEffect(() => {
    if (state?.openEndTenancyModalFromApp) {
      showEndTenancyModal({ listingId: state.listing_id })
    }
    if (state?.openPaymentRequestModalFromApp) {
      showPaymentDetailsModal({ listingId: state.listing_id, paymentId: state.payment_id })
    }
  }, [state])

  // TODO: Removing the load of appointments. It patches
  // the listing state causing a bug in the editListingSelector
  // const listing_id = timeline?.listing_id
  // useEffect(() => {
  //   if (listing_id) {
  //     dispatch.listing.getAppointments(listing_id)
  //   }
  // }, [dispatch.listing, listing_id])

  const searchParamInTheUrl = !!searchParams?.search

  useEffect(() => {
    async function load() {
      dispatch.chat.loadTimeline({ timelineId, isSearchResult: searchParamInTheUrl })
    }
    load()
  }, [timelineId, dispatch.chat, searchParamInTheUrl])

  const loadMoreTop = useCallback(async () => {
    if (loading) return
    if (lastId === undefined && lastEventId === undefined) return
    setLoading(true)

    const method = isSearchResult ? dispatch.chat.getEventsSearchUp : dispatch.chat.getEventsUp
    await method({
      timelineRoomId: params.timelineId,
      lastEventId: lastId || lastEventId
    })
    setLoading(false)
  }, [dispatch.chat, loading, lastId, lastEventId, params.timelineId, isSearchResult])

  const loadMoreBottom = useCallback(async () => {
    if (latestId === undefined) return
    setLoadingBottom(true)

    const method = isSearchResult ? dispatch.chat.getEventsSearchDown : dispatch.chat.getEventsDown
    await method({
      timelineRoomId: params.timelineId,
      lastEventId: latestId
    })
    setLoadingBottom(false)
  }, [dispatch.chat, latestId, loadingBottom, params.timelineId, isSearchResult])

  const loadInitialSearchEvents = useCallback(
    async targetMessageId => {
      setLoading(true)
      setLoadingBottom(true)
      if (targetMessageId)
        await dispatch.chat.loadInitialSearchEvents({
          timelineId: params.timelineId,
          targetMessageId
        })
      setLoading(false)
      setLoadingBottom(false)
      const target = document.getElementById(`chat-event-message-${targetMessageId}`)
      target &&
        target.scrollIntoView({
          block: 'center'
        })
      scrollMoreEnabled.current = true
    },
    [dispatch.chat, params.timelineId]
  )
  useEffect(() => {
    return () => {
      setLoading(false)
      setLoadingBottom(false)
      dispatch.chat.clearCurrentChat()
    }
  }, [dispatch.chat])

  useEffect(() => {
    loadedMore.current = false
  }, [params.timelineId, isSearchResult, targetMessageId])

  useEffect(() => {
    if (timelineLoaded) {
      dispatch.chat.setTimelineRead(params.timelineId)
      return
    }
  }, [timelineLoaded, params.timelineId, dispatch.chat])

  useEffect(() => {
    if (!timeline && !targetIsLastMessageFirstLoad) return
    loadedMore.current = true
    if (isSearchResult) {
      const target = document.getElementById(`chat-event-message-${targetMessageId}`)
      if (target) {
        return target.scrollIntoView({ block: 'center' })
      }
      loadInitialSearchEvents(targetMessageId)
    } else {
      loadMoreTop()
      scrollMoreEnabled.current = true
    }

    dispatch.chat.setTimelineRead(params.timelineId)
  }, [
    dispatch.chat,
    loadInitialSearchEvents,
    isSearchResult,
    params.timelineId,
    timelineLoaded,
    targetMessageId
  ])

  const handleScrollTop = async e => {
    if (!scrollMoreEnabled.current && !isSearchResult) return
    const oldFirstMessage = document.querySelector('.chat-event-message')
    if (!oldFirstMessage) return
    const getScrollPosition = buildScrollPosition(oldFirstMessage)
    await loadMoreTop()
    const scrollPosition = getScrollPosition()
    e.target.scrollTo(0, scrollPosition)
  }

  const handleScrollBottom = async e => {
    if (!scrollMoreEnabled.current && !isSearchResult) return

    const scrollPosition = e.target.scrollTop
    if (isSearchResult) await loadMoreBottom()
    e.target.scrollTo(0, scrollPosition)
  }

  const handleInviteToApply = async () => {
    if (sendingInvite) return
    setSendingInvite(true)
    const { response } = await dispatch.chat.inviteToApply({
      timelineRoomId: params.timelineId
    })
    if (response.ok) {
      //  User should only be able to "Invite to Apply" once every 5 seconds
      setTimeout(() => {
        setSendingInvite(false)
      }, 5000)
    }
  }

  const handleSendMessage = message => {
    dispatch.chat.sendMessage({
      message,
      timelineRoomId: params.timelineId
    })
  }

  const handleSendFile = async files => {
    const sendAction = ['image/png', 'image/jpg', 'image/jpeg'].includes(head(files)?.type)
      ? dispatch.chat.sendImage
      : dispatch.chat.sendFile

    sendAction({
      file: head(files),
      timelineRoomId: params.timelineId
    })
  }

  const contract = timeline?.contracts || {}

  if (!timeline || !timeline.units) return null

  const { users, primary_user_ids } = timeline
  const primaryOther = getPrimaryOther(users, primary_user_ids)
  const name = formatName(primaryOther)
  const chatAvatar = isLandlord
    ? getImageUrl(primaryOther?.avatar?.aws_s3_key, {
        width: 80,
        height: 80
      })
    : getChatAvatar(users, primary_user_ids, 80)

  const isRented = timeline?.state_machine === 'rented'

  // It seems that in some cases the building can be null
  const city = `, ${timeline?.buildings?.city}` || ''

  const unitHidden = hideUnit(timeline.units, contract)

  const getLandlordAddress = () => {
    const unitNumber = timeline.units.unit_number
    const streetAddress = timeline.buildings.full_street_name
    const address = unitNumber
      ? `${unitNumber} - ${streetAddress}${city}`
      : `${streetAddress}${city}`

    return address
  }

  const getTenantHouseAddress = () => {
    const unitNumber = unitHidden ? t('common.house', 'House') : timeline.units.unit_number
    const streetAddress = unitHidden
      ? timeline.buildings.street_name
      : timeline.buildings.full_street_name
    const address = unitNumber ? `${unitNumber} - ${streetAddress}` : streetAddress

    return `${address}${city}`
  }

  const getTenantApartmentAddress = () => {
    const unitNumber = unitHidden ? null : timeline.units.unit_number
    const streetAddress = timeline.buildings.full_street_name
    const address = unitNumber ? `${unitNumber} - ${streetAddress}` : streetAddress

    return `${address}${city}`
  }

  const getTenantAddress = () => {
    const isHouse = timeline.buildings.building_type_txt_id === 'house'
    const address = isHouse ? getTenantHouseAddress() : getTenantApartmentAddress()

    return address
  }

  const closeMessageDetailsDrawer = () => {
    setIsMessageDrawerOpen(false)
    setActiveTimelineEvent(null)
  }
  const timelineUsers = mergeAll([
    ...timeline.users.them.map(user => {
      return { [user.id]: { id: user.id, liv_verified_state: user.liv_verified_state } }
    }),
    ...timeline.users.us.map(user => {
      return { [user.id]: { id: user.id, liv_verified_state: user.liv_verified_state } }
    })
  ])

  const them = timeline?.users?.them
  const us = timeline?.users?.us
  const address = role === 'tenant' ? getTenantAddress() : getLandlordAddress()
  const listingId = timeline?.listing_id
  const hasApplied = !!timeline?.applications?.id

  const listingUrl = getUrl({
    building_type: timeline.buildings.building_type_txt_id,
    city: timeline.buildings.city,
    listing_id: timeline.listing_id
  })

  const isLocked = timeline?.state_machine === 'locked'
  const showInviteToApply = isLandlord && !hasApplied
  const showViewApplication = isLandlord && hasApplied

  const showMessageDetailsDrawer = timelineEvent => {
    setActiveTimelineEvent(timelineEvent)
    setIsMessageDrawerOpen(true)
  }

  const getAddressUrl = () => {
    if (isLandlord) {
      return `/listing/${timeline.listing_id}`
    } else {
      return timeline.state_machine === 'rented'
        ? `/rental/${timeline.listing_id}`
        : `${WEB_URL}${listingUrl}`
    }
  }

  const handleShowMembers = () => {
    showTimelineMembersModal({
      timeline,
      isLocked,
      isRented,
      history,
      address
    })
  }

  return (
    <>
      <MessageDetailsDrawer
        timelineUsers={timelineUsers}
        isOpen={isMessageDrawerOpen}
        activeTimelineEvent={activeTimelineEvent}
        close={() => closeMessageDetailsDrawer()}
      />
      <BaseChatRoom
        loading={loading}
        loadingBottom={loadingBottom}
        avatarUrl={chatAvatar}
        name={name}
        address={address}
        otherUser={primaryOther}
        events={timelineEvents}
        handleSendMessage={handleSendMessage}
        handleSendFile={handleSendFile}
        handleInviteToApply={handleInviteToApply}
        sendingInvite={sendingInvite}
        listing={listing}
        onScrollTop={handleScrollTop}
        onScrollBottom={handleScrollBottom}
        handleBack={handleBack}
        timeline={timeline}
        presetMessages={presetMessages}
        isRented={isRented}
        showMessageDetailsDrawer={showMessageDetailsDrawer}
        them={them}
        us={us}
        listingUrl={listingUrl}
        listingId={listingId}
        getAddressUrl={getAddressUrl}
        handleShowMembers={handleShowMembers}
        isLocked={isLocked}
        showInviteToApply={showInviteToApply}
        showViewApplication={showViewApplication}
        hasApplied={hasApplied}
        is_favourite={is_favourite}
      />
    </>
  )
}

export default memo(ChatRoom)
